import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fetchClientDiagnostics,
  flushQueuedDiagnosticEvents,
  getLastDiagnosticsSnapshot,
  getQueuedDiagnosticEventCount,
  sendCurrentDiagnosticsSnapshot,
} from '../lib/clientDiagnostics';
import { getSupabaseCircuitState, resetSupabaseCircuit } from '../lib/supabaseCircuitBreaker';
import { recordRecoveryAction, refreshAppShell } from '../lib/appRecovery';
import { Sentry } from '../lib/sentry';
import { showToast } from '../lib/toast';

const severityColours = {
  info: '#8ea0b8',
  warning: '#d4a84a',
  error: '#d06868',
  critical: '#ff4d5d',
};

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortText(value, max = 140) {
  const text = String(value || '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function JsonBlock({ value }) {
  if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) return null;
  return (
    <pre style={{
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      margin: '8px 0 0',
      padding: '8px',
      borderRadius: 8,
      background: '#071326',
      border: '1px solid #263653',
      color: '#a8b8cc',
      fontSize: 11,
      lineHeight: 1.35,
      fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
      maxHeight: 180,
      overflow: 'auto',
    }}>
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function DiagnosticRow({ event }) {
  const [open, setOpen] = useState(false);
  const colour = severityColours[event.severity] || severityColours.warning;
  return (
    <div style={{ borderBottom: '1px solid #263653', padding: '12px 0' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: colour, flexShrink: 0 }} />
          <span style={{ color: colour, fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 }}>
            {event.severity || 'warning'}
          </span>
          <span style={{ color: '#6a80a0', fontSize: 11 }}>{formatTime(event.created_at)}</span>
          {event.online === false && <span style={{ color: '#d06868', fontSize: 11, fontWeight: 800 }}>offline</span>}
        </div>
        <div style={{ color: '#f0f2f6', fontSize: 14, fontWeight: 800, lineHeight: 1.25 }}>
          {event.event_type}
        </div>
        <div style={{ color: '#a8b8cc', fontSize: 12, lineHeight: 1.35, marginTop: 3 }}>
          {shortText(event.message || event.metadata?.error?.message || 'No message')}
        </div>
        {(event.da_number || event.council || event.surface) && (
          <div style={{ color: '#6a80a0', fontSize: 11, lineHeight: 1.35, marginTop: 5 }}>
            {[event.da_number, event.council, event.surface, event.page].filter(Boolean).join(' · ')}
          </div>
        )}
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          <div style={{ color: '#6a80a0', fontSize: 11, lineHeight: 1.45 }}>
            <div>User: {event.user_email || event.user_id}</div>
            <div>Path: {event.url_path || '-'}</div>
            <div>Service worker: {event.service_worker_version || '-'}</div>
          </div>
          <JsonBlock value={{
            circuit: event.circuit,
            cache: event.cache,
            auth: event.auth,
            viewport: event.viewport,
            timing: event.timing,
            metadata: event.metadata,
          }} />
        </div>
      )}
    </div>
  );
}

export default function AdminDiagnosticsPanel({ onClose }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [snapshotSending, setSnapshotSending] = useState(false);
  const [queueCount, setQueueCount] = useState(() => getQueuedDiagnosticEventCount());
  const [lastSnapshot, setLastSnapshot] = useState(() => getLastDiagnosticsSnapshot());
  const circuit = useMemo(() => getSupabaseCircuitState(), [status, loading]);

  const loadEvents = async () => {
    setLoading(true);
    setStatus('');
    try {
      const rows = await fetchClientDiagnostics(100);
      setEvents(rows);
    } catch (err) {
      console.warn('[SiteScope] Diagnostics load failed:', err.message || err);
      Sentry.captureException(err, {
        tags: { sitescope_area: 'admin_diagnostics', sitescope_action: 'load_events' },
      });
      setStatus(err.message || 'Could not load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleSendSnapshot = async () => {
    setSnapshotSending(true);
    setStatus('Capturing snapshot...');
    try {
      const result = await sendCurrentDiagnosticsSnapshot(user, { action: 'manual_admin_snapshot' });
      setQueueCount(result.queue_count ?? getQueuedDiagnosticEventCount());
      setLastSnapshot(getLastDiagnosticsSnapshot());
      setStatus(result.message || (result.sent ? 'Snapshot sent.' : 'Snapshot queued.'));
      showToast(result.sent ? 'Diagnostics snapshot sent.' : 'Diagnostics snapshot saved and queued.');
      await loadEvents();
    } catch (err) {
      const message = err?.message || 'Snapshot could not be sent';
      console.warn('[SiteScope] Diagnostics snapshot failed:', message);
      Sentry.captureException(err, {
        tags: { sitescope_area: 'admin_diagnostics', sitescope_action: 'send_snapshot' },
      });
      setStatus(`Snapshot failed: ${message}`);
    } finally {
      setSnapshotSending(false);
    }
  };

  const handleResetCircuit = () => {
    resetSupabaseCircuit();
    recordRecoveryAction('reset_supabase_circuit', getSupabaseCircuitState());
    setStatus('Supabase circuit reset on this device.');
  };

  const handleFlushQueue = async () => {
    setStatus('Sending queued diagnostics...');
    try {
      const flushed = await flushQueuedDiagnosticEvents();
      const nextCount = getQueuedDiagnosticEventCount();
      setQueueCount(nextCount);
      setStatus(flushed > 0
        ? `Sent ${flushed} queued diagnostic event(s). ${nextCount} still queued.`
        : `No queued diagnostics sent. ${nextCount} still queued.`);
      if (flushed > 0) await loadEvents();
    } catch (err) {
      setStatus(`Queued diagnostics could not be sent: ${err?.message || err}`);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 2200,
      background: '#081525',
      color: '#f0f2f6',
      fontFamily: "'DM Sans', sans-serif",
      overflowY: 'auto',
    }}>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        background: '#081525',
        borderBottom: '1px solid #263653',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 11, color: '#d4a84a', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 }}>
            SiteScope internal
          </div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>Diagnostics</div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close diagnostics"
          style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #30404e', background: 'transparent', color: '#f0f2f6', fontSize: 18, cursor: 'pointer' }}
        >
          x
        </button>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '16px 16px 48px' }}>
        <div style={{
          background: '#0a1b36',
          border: '1px solid #30404e',
          borderRadius: 8,
          padding: 14,
          marginBottom: 12,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div>
              <div style={{ color: '#6a80a0', fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Online</div>
              <div style={{ color: navigator.onLine ? '#5aba6a' : '#d06868', fontSize: 14, fontWeight: 900 }}>
                {navigator.onLine ? 'yes' : 'no'}
              </div>
            </div>
            <div>
              <div style={{ color: '#6a80a0', fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Circuit</div>
              <div style={{ color: circuit.open ? '#d06868' : '#5aba6a', fontSize: 14, fontWeight: 900 }}>
                {circuit.open ? `open ${Math.ceil(circuit.remaining_ms / 1000)}s` : `closed · ${circuit.failures} failures`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={snapshotSending ? undefined : handleSendSnapshot} disabled={snapshotSending} style={buttonStyle('#d4a84a', '#081525', snapshotSending)}>{snapshotSending ? 'Sending...' : 'Send snapshot'}</button>
            <button onClick={loadEvents} style={buttonStyle('#30404e', '#f0f2f6')}>Refresh</button>
            <button onClick={handleFlushQueue} style={buttonStyle('#30404e', '#f0f2f6')}>Send queue</button>
            <button onClick={handleResetCircuit} style={buttonStyle('#30404e', '#f0f2f6')}>Reset circuit</button>
            <button onClick={refreshAppShell} style={buttonStyle('#30404e', '#f0f2f6')}>Repair app</button>
          </div>
          {status && <div style={{ color: '#a8b8cc', fontSize: 12, marginTop: 10, lineHeight: 1.35 }}>{status}</div>}
          <div style={{ color: '#6a80a0', fontSize: 11, marginTop: 8, lineHeight: 1.35 }}>
            Queued diagnostics on this device: {queueCount}
            {lastSnapshot?.captured_at ? ` · Last local snapshot: ${formatTime(lastSnapshot.captured_at)} (${lastSnapshot.delivery?.status || 'captured'})` : ''}
          </div>
        </div>

        <div style={{
          background: '#0a1b36',
          border: '1px solid #30404e',
          borderRadius: 8,
          padding: '2px 14px 4px',
        }}>
          {loading && <div style={{ padding: '18px 0', color: '#a8b8cc', fontSize: 13 }}>Loading diagnostics...</div>}
          {!loading && events.length === 0 && <div style={{ padding: '18px 0', color: '#a8b8cc', fontSize: 13 }}>No diagnostics recorded yet.</div>}
          {!loading && events.map(event => <DiagnosticRow key={event.id} event={event} />)}
        </div>
      </div>
    </div>
  );
}

function buttonStyle(background, color, disabled = false) {
  return {
    border: '1px solid #30404e',
    background,
    color,
    borderRadius: 8,
    padding: '9px 11px',
    fontSize: 12,
    fontWeight: 900,
    fontFamily: "'DM Sans', sans-serif",
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}
